// src/components/wordbookGroup/WorkbookGroupShow.tsx
import { FC } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Box,
    Button,
    Card,
    CardContent,
    CircularProgress,
    Typography,
    Grid,
    Chip,
    Alert,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Divider,
    IconButton,
} from '@mui/material';
import {
    ArrowBack,
    Edit,
    Delete,
    Visibility as VisibilityIcon,
    VisibilityOff,
    MenuBook,
    Restore,
    DeleteForever,
    LibraryAdd,
    LibraryBooks,
} from '@mui/icons-material';
import {
    useWordbookGroup,
    useDeleteWordbookGroup,
    usePermanentDeleteWordbookGroup,
    useRestoreWordbookGroup,
    useToggleWordbookGroupVisibility,
} from '../../hooks/useWordbookGroup';
import { Visibility } from '../../types/wordbookGroup';
import { formatKstDate, formatKstDateTime } from '../../utils/dateTime';

export const WordbookGroupShow: FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const groupId = parseInt(id || '0');

    const { data: group, isLoading, error } = useWordbookGroup(groupId);
    const deleteGroup = useDeleteWordbookGroup();
    const permanentDelete = usePermanentDeleteWordbookGroup();
    const restoreGroup = useRestoreWordbookGroup();
    const toggleVisibility = useToggleWordbookGroupVisibility();

    const handleDelete = () => {
        if (confirm('그룹을 삭제하시겠습니까? 그룹에 속한 단어장들은 그룹에서 제외됩니다.')) {
            deleteGroup.mutate(groupId, {
                onSuccess: () => {
                    navigate('/wordbook-group');
                },
            });
        }
    };

    const handlePermanentDelete = () => {
        if (confirm('⚠️ 영구 삭제하시겠습니까? 이 작업은 되돌릴 수 없습니다.')) {
            permanentDelete.mutate(groupId, {
                onSuccess: () => {
                    navigate('/wordbook-group');
                },
            });
        }
    };

    const handleRestore = () => {
        restoreGroup.mutate(groupId);
    };

    const handleToggleVisibility = () => {
        toggleVisibility.mutate(groupId);
    };

    if (isLoading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" p={4}>
                <CircularProgress />
            </Box>
        );
    }

    if (error || !group) {
        return (
            <Box p={3}>
                <Alert severity="error">
                    {error?.message || '그룹을 찾을 수 없습니다.'}
                </Alert>
            </Box>
        );
    }

    // wordbooks가 null일 수 있으므로 안전하게 처리
    const wordbooks = group.wordbooks || [];
    const wordbooksCount = wordbooks.length;

    return (
        <Box p={3}>
            {/* 헤더 */}
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                <Box display="flex" alignItems="center" gap={2}>
                    <Button
                        startIcon={<ArrowBack />}
                        onClick={() => navigate('/wordbook-group')}
                    >
                        목록으로
                    </Button>
                    <Typography variant="h4">
                        {group.title}
                    </Typography>
                    {group.deletedAt && (
                        <Chip label="삭제됨" color="error" />
                    )}
                    {group.libraryId != null && (
                        <Chip
                            icon={<LibraryBooks />}
                            label="라이브러리 등록됨"
                            color="secondary"
                        />
                    )}
                </Box>
                <Box display="flex" gap={1}>
                    {group.deletedAt ? (
                        <>
                            <Button
                                startIcon={<Restore />}
                                variant="contained"
                                color="success"
                                onClick={handleRestore}
                            >
                                복원
                            </Button>
                            <Button
                                startIcon={<DeleteForever />}
                                variant="outlined"
                                color="error"
                                onClick={handlePermanentDelete}
                            >
                                영구 삭제
                            </Button>
                        </>
                    ) : (
                        <>
                            {/* 라이브러리 버튼 - libraryId 유무에 따라 등록/수정 */}
                            {group.libraryId != null ? (
                                // 이미 등록된 경우: 수정 버튼
                                <Button
                                    startIcon={<Edit />}
                                    variant="outlined"
                                    color="secondary"
                                    onClick={() => navigate(`/library/${group.libraryId}/edit`)}
                                >
                                    라이브러리 수정
                                </Button>
                            ) : (
                                // 미등록인 경우: 등록 버튼
                                <Button
                                    startIcon={<LibraryAdd />}
                                    variant="outlined"
                                    color="secondary"
                                    onClick={() => navigate(`/library/create?groupId=${groupId}`)}
                                >
                                    라이브러리 등록
                                </Button>
                            )}
                            <Button
                                startIcon={group.visibility === Visibility.VISIBLE ? <VisibilityIcon /> : <VisibilityOff />}
                                onClick={handleToggleVisibility}
                            >
                                {group.visibility === Visibility.VISIBLE ? '노출 중' : '숨김'}
                            </Button>
                            <Button
                                startIcon={<Edit />}
                                variant="outlined"
                                onClick={() => navigate(`/wordbook-group/${groupId}/edit`)}
                            >
                                수정
                            </Button>
                            <Button
                                startIcon={<Delete />}
                                variant="outlined"
                                color="error"
                                onClick={handleDelete}
                            >
                                삭제
                            </Button>
                        </>
                    )}
                </Box>
            </Box>

            <Grid container spacing={3}>
                {/* 기본 정보 카드 */}
                <Grid size={{ xs: 12, md: 6 }}>
                    <Card>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>
                                기본 정보
                            </Typography>
                            <Divider sx={{ mb: 2 }} />
                            {group.thumbnailUrl && (
                                <Box mb={2}>
                                    <img
                                        src={group.thumbnailUrl}
                                        alt={group.title}
                                        style={{
                                            width: '100%',
                                            maxWidth: 150,
                                            height: 'auto',
                                            borderRadius: 8,
                                        }}
                                    />
                                </Box>
                            )}
                            <Box display="flex" flexDirection="column" gap={2}>
                                <Box>
                                    <Typography variant="caption" color="text.secondary">
                                        ID
                                    </Typography>
                                    <Typography variant="body1">{group.id}</Typography>
                                </Box>
                                <Box>
                                    <Typography variant="caption" color="text.secondary">
                                        제목
                                    </Typography>
                                    <Typography variant="body1">{group.title}</Typography>
                                </Box>
                                <Box>
                                    <Typography variant="caption" color="text.secondary">
                                        소유자
                                    </Typography>
                                    <Typography variant="body1">
                                        {group.ownerName || '-'}
                                        {group.ownerEmail && (
                                            <Typography variant="caption" color="text.secondary" display="block">
                                                {group.ownerEmail}
                                            </Typography>
                                        )}
                                    </Typography>
                                </Box>
                                <Box>
                                    <Typography variant="caption" color="text.secondary">
                                        언어 쌍
                                    </Typography>
                                    <Typography variant="body1">
                                        {group.headLang} → {group.meaningLang}
                                    </Typography>
                                </Box>
                                {/* 라이브러리 ID 표시 */}
                                {group.libraryId != null && (
                                    <Box>
                                        <Typography variant="caption" color="text.secondary">
                                            라이브러리 ID
                                        </Typography>
                                        <Typography variant="body1">
                                            <Chip
                                                label={`#${group.libraryId}`}
                                                size="small"
                                                color="secondary"
                                                onClick={() => navigate(`/library/${group.libraryId}`)}
                                                clickable
                                            />
                                        </Typography>
                                    </Box>
                                )}
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>

                {/* 통계 정보 카드 */}
                <Grid size={{ xs: 12, md: 6 }}>
                    <Card>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>
                                통계 정보
                            </Typography>
                            <Divider sx={{ mb: 2 }} />
                            <Box display="flex" flexDirection="column" gap={2}>
                                <Box>
                                    <Typography variant="caption" color="text.secondary">
                                        단어장 수
                                    </Typography>
                                    <Typography variant="h4" color="primary">
                                        {group.totalWordbooks.toLocaleString()}
                                    </Typography>
                                </Box>
                                <Box>
                                    <Typography variant="caption" color="text.secondary">
                                        총 단어 수
                                    </Typography>
                                    <Typography variant="h4" color="secondary">
                                        {group.totalWords.toLocaleString()}
                                    </Typography>
                                </Box>
                                <Box display="flex" gap={1}>
                                    <Chip
                                        label={group.visibility === Visibility.VISIBLE ? '노출' : '숨김'}
                                        color={group.visibility === Visibility.VISIBLE ? 'success' : 'default'}
                                        size="small"
                                    />
                                    {group.libraryId != null && (
                                        <Chip label="라이브러리" color="secondary" size="small" />
                                    )}
                                </Box>
                                <Box>
                                    <Typography variant="caption" color="text.secondary">
                                        생성일
                                    </Typography>
                                    <Typography variant="body2">
                                        {formatKstDateTime(group.createdAt)}
                                    </Typography>
                                </Box>
                                <Box>
                                    <Typography variant="caption" color="text.secondary">
                                        수정일
                                    </Typography>
                                    <Typography variant="body2">
                                        {formatKstDateTime(group.updatedAt)}
                                    </Typography>
                                </Box>
                                {group.deletedAt && (
                                    <Box>
                                        <Typography variant="caption" color="text.secondary">
                                            삭제일
                                        </Typography>
                                        <Typography variant="body2" color="error">
                                            {formatKstDateTime(group.deletedAt)}
                                        </Typography>
                                    </Box>
                                )}
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>

                {/* 단어장 목록 */}
                <Grid size={{ xs: 12 }}>
                    <Card>
                        <CardContent>
                            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                                <Typography variant="h6">
                                    포함된 단어장 ({wordbooksCount})
                                </Typography>
                                {!group.deletedAt && (
                                    <Button
                                        variant="contained"
                                        size="small"
                                        startIcon={<LibraryAdd />}
                                        onClick={() => navigate(`/wordbook/create/${groupId}`)}
                                    >
                                        단어장 등록
                                    </Button>
                                )}
                            </Box>
                            <Divider sx={{ mb: 2 }} />
                            {wordbooksCount === 0 ? (
                                <Typography color="text.secondary" textAlign="center" py={4}>
                                    이 그룹에 속한 단어장이 없습니다.
                                </Typography>
                            ) : (
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>순서</TableCell>
                                            <TableCell>제목</TableCell>
                                            <TableCell>언어</TableCell>
                                            <TableCell>단어 수</TableCell>
                                            <TableCell>노출</TableCell>
                                            <TableCell>상태</TableCell>
                                            <TableCell>생성일</TableCell>
                                            <TableCell>액션</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {wordbooks.map((wordbook) => (
                                            <TableRow
                                                key={wordbook.id}
                                                hover
                                                sx={{
                                                    cursor: 'pointer',
                                                    opacity: wordbook.deletedAt ? 0.5 : 1,
                                                    backgroundColor: wordbook.deletedAt ? '#ffebee' : 'inherit',
                                                }}
                                                onClick={() => navigate(`/wordbook/${wordbook.id}`)}
                                            >
                                                <TableCell>{wordbook.groupSortOrder + 1}</TableCell>
                                                <TableCell>
                                                    <Typography variant="body2" fontWeight={500}>
                                                        {wordbook.title}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Typography variant="body2">
                                                        {wordbook.headLang} → {wordbook.meaningLang}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Chip
                                                        label={wordbook.totalWords.toLocaleString()}
                                                        size="small"
                                                        color={wordbook.totalWords > 0 ? 'primary' : 'default'}
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    {wordbook.visibility === Visibility.VISIBLE ? (
                                                        <VisibilityIcon color="primary" fontSize="small" />
                                                    ) : (
                                                        <VisibilityOff fontSize="small" />
                                                    )}
                                                </TableCell>
                                                <TableCell>
                                                    {wordbook.deletedAt ? (
                                                        <Chip label="삭제됨" size="small" color="error" />
                                                    ) : (
                                                        <Chip label="활성" size="small" color="success" />
                                                    )}
                                                </TableCell>
                                                <TableCell>
                                                    <Typography variant="body2" color="text.secondary">
                                                        {formatKstDate(wordbook.createdAt)}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell onClick={(e) => e.stopPropagation()}>
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => navigate(`/wordbook/${wordbook.id}`)}
                                                        title="상세보기"
                                                    >
                                                        <MenuBook fontSize="small" />
                                                    </IconButton>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            )}
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>
        </Box>
    );
};
